/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { ServiceType } from "constants/feedback"
import { BusinessType } from "types/models/menu"

export type ReviewReply = {
  replyID?: string // can be undefined if mocked by FE after first submission
  createdAt: string
  updatedAt: string
  description: string
  uneditableReason: number
  repliedBy: string
}

export enum ReviewStatus {
  UNKNOWN = "UNKNOWN",
  PUBLIC = "PUBLIC",
  REPORTED = "REPORTED",
  REMOVED = "REMOVED",
  PRIVATE = "PRIVATE",
}

export enum ReviewReplyReason {
  ReviewUnreplyableReasonReplyable = 0,
  ReviewUnreplyableReasonNonPublicReview = 1,
  ReviewUnreplyableReasonRoleForbiddenAccess = 2,
  ReviewUnreplyableReasonReplyCountLimitReached = 3,
  ReviewUnreplyableReasonEditDurationLimitReached = 4,
  ReviewUnreplyableReasonEditCountLimitReached = 5,
  ReviewUnreplyableMerchantNotSupportMexReplies = 6,
  ReviewUnreplyableDineOutReviewsNotSupportMexReplies = 7,
}

export enum ReviewUnReportableReason {
  ReviewUnreportableReasonReportable = 0,
  ReviewUnreportableReasonReviewIsRejected = 1,
  ReviewUnreportableReasonReviewIsReported = 2,
  ReviewUnreportableReasonReviewIsRemoved = 3,
  ReviewUnreportableReasonRoleForbiddenAccess = 4,
  ReviewUnreportableReasonReviewStatusIsUnknown = 5,
  ReviewUnreportableReasonReportLimit = 6,
  ReviewUnreportableReasonReportIncorrectLimit = 7,
  ReviewUnreportableReasonReportLimitExtended = 8,
  ReviewUnreportableReasonReportIncorrectLimitExtended = 9,
  ReviewUnreportableReasonReportDisabled = 10,
}

export type Review = {
  reviewID: string
  createdAt: string
  rating: number
  description: string
  /** @deprecated */
  orderID: string
  bookingCode: string
  eaterName: string
  /** @deprecated */
  isReportedBefore: boolean
  status: ReviewStatus
  orderedItems?: string[]
  isNew: boolean
  reportedAt: string
  unreportableReason: ReviewUnReportableReason
  unreplyableReason: ReviewReplyReason
  reviewReplies: ReviewReply[]
  paxReviewImageUrls: string[]
  contentLastModifiedAt: string
  merchantName: string
  merchantID: string
  serviceType?: ServiceType
}

export type ListFeedbackResponse = {
  reviews: Review[]
  hasMore: boolean
  nextToken?: string
}

type Rating = {
  itemID: string
  itemLabel: string
  itemSubLabel: string
  rating: string
}

type RatingDistribution = {
  score: number
  countPercentage: number
}

type ComponentRating = {
  ratings: Rating[]
}

type MinMaxRatingDetails = {
  minStoreRating: number
  minStoreName: string[]
  maxStoreRating: number
  maxStoreName: string[]
}

export type FeedbackOverview = {
  aggregatedRatingScore: number
  ratingCount: number
  ratingDistribution: RatingDistribution[]
  leaveAReviewEntryPointDeeplink: string
  componentRating?: ComponentRating
  minMaxRatingDetails?: MinMaxRatingDetails
}

export type FeedbackOverviews = {
  [key in ServiceType]: FeedbackOverview
}

export type FeedbackOverviewResponse = {
  feedbackOverview: FeedbackOverview
}

export type RequestCsvReportResponse = {
  ref_id: string
}

export type GetCsvReportStatusResponse = {
  ref_id: string
  source: string
  status: "UNKNOWN" | "IN_PROGRESS" | "SUCCESS" | "FAILED"
  s3_url?: string
}

export enum ReviewReplyError {
  MERCHANT_IS_NOT_ROLLED_OUT_TO_MERCHANT_REPLIES = "merchant_is_not_rolled_out_to_merchant_replies",
  ROLE_NO_ACCESS_TO_MEX_REPLIES = "role_no_access_to_mex_replies",
  REPLY_NOT_BELONG_TO_MERCHANT = "reply_not_belong_to_merchant",
  NON_PUBLIC_REVIEW_CANNOT_BE_REPLIED = "non_public_review_cannot_be_replied",
  CONTENT_LENGTH_LIMIT_EXCEEDED = "content_length_limit_exceeded",
  CONTENT_CONTAIN_PROFANITY = "content_contain_profanity",
  REPLY_COUNT_LIMIT_EXCEEDED = "reply_count_limit_exceeded",
  EDIT_COUNT_LIMIT_EXCEEDED = "edit_count_limit_exceeded",
  EDIT_DURATION_LIMIT_EXCEEDED = "edit_duration_limit_exceeded",
}

export type CreateReviewReplyRequest = {
  reviewID: string
  description: string
}

export type CreateReviewReplyResponse = {
  replyID: string
  message: string // to hold error message returned by BE
}

export type UpdateReviewReplyRequest = {
  reviewID: string
  replyID: string
  description: string
}

export type UpdateReviewReplyResponse = {
  message: string // to hold error message returned by BE
}

export type ListReviewsRequest = {
  pageIndex?: number
  pageSize?: number
  orderBy?: string
  asc?: boolean
  nextToken?: string
  // Top filter values
  merchantIDs?: string[]
  cityName?: string // Note: this value is only to filter merchantIDs in FE
  cityID?: number // Note: this value is to filter merchantIDs from new get merchantIDS endpoint
  businessType?: BusinessType // Note: this value is only to filter merchantIDs in FE
  serviceType?: ServiceType
  // Lower filter values
  startDate?: string
  endDate?: string
  status?: ReviewStatus[]
  includeEmptyReviews?: boolean
}

export type APIListReviewsResponse = {
  reviews: Review[] | null
  hasMore?: boolean
  nextToken?: string
}

export type ExtraReviewsFilterConfig = Omit<
  ListReviewsRequest,
  "pageIndex" | "pageSize" | "merchantIDs"
>

export type ReportReviewRequest = {
  reviewID: string
  reasons: string[]
  imageTokens: string[]
  imageUrls: string[]
  description: string
}

export type RequestCsvReportBody = {
  startDate?: string
  endDate?: string
  queryNames: string[]
  extraFilters: Pick<
    ListReviewsRequest,
    "cityName" | "serviceType" | "merchantIDs"
  > & {
    businessTypeFilter?: BusinessType
    reviewStatus?: ReviewStatus[]
  }
}
